import { forwardRef, Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { CustomerInfoLoadIfNormalUserGuard } from 'app/core/guards/customer-info-load-if-normal-user.guard';
import { DependencyTreeCanActivate } from 'app/core/guards/dependency-tree.guard';
import { FlowFilterValuesLoadGuard } from 'app/core/guards/flow-filter-values-load.guard';
import { AuthorizationGuard } from 'app/core/store/session/authorization/authorization.resolve';
import { logger } from 'app/core/util/logger';
import { Observable, of } from 'rxjs';
import * as fromRoot from '..';
import { FlowDetailCreateStartAction } from './flow-detail.actions';
import { goAction } from '../router/router.actions';
import { first, map, switchMap } from 'rxjs/operators';

/**
 * Guard to async load a flow before following a route
 */
@Injectable({ providedIn: 'root' })
export class CreateFlowGuard implements CanActivate, DependencyTreeCanActivate {
  private store: Store<fromRoot.RootState>;
  private customerInfoLoadIfNormalUserGuard: CustomerInfoLoadIfNormalUserGuard;

  constructor(@Inject(forwardRef(() => Store)) store, @Inject(forwardRef(() => CustomerInfoLoadIfNormalUserGuard)) customerInfoLoadIfNormalUserGuard) {
    this.store = store;
    this.customerInfoLoadIfNormalUserGuard = customerInfoLoadIfNormalUserGuard;
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    logger.debug(`In CreateFlowGuard`);
    return this.customerInfoLoadIfNormalUserGuard.canActivate(route, state).pipe(
      first(),
      switchMap(customerInfoLoaded => {
        return this.store.pipe(
          first(),
          map(state => state.session.authorization.data.FLOW.CREATE),
          switchMap(canCreateFlow => {
            if (!canCreateFlow) {
              this.store.dispatch(goAction({ path: '/' }));
              return of(false);
            }

            return this.store.pipe(
              first(),
              map(state => ({
                selectedCustomer: state.session.selectedCustomer.data,
                isSuperUser: state.session.userContext.isSuperUser,
              })),
              switchMap(({ selectedCustomer, isSuperUser }) => {
                this.store.dispatch(new FlowDetailCreateStartAction({ selectedCustomer, isSuperUser }));
                return of(true);
              })
            );
          })
        );
      })
    );
  }

  getDependencies(): any {
    return [FlowFilterValuesLoadGuard, AuthorizationGuard];
  }

  getDependencyRouteData(): any {
    return {
      authorization: 'FLOW.CREATE',
    };
  }
}
